/* eslint-disable no-var */
import { EventMethod } from './enums/EventMethod'
import { EventType } from './enums/EventType'
import type {
  INativeResponse,
  IBidResponse,
  IEventTracker
} from '../../typings/IPrebid'
import { nativeWrapperSelector } from './hasNative'
import { adLogo } from './templates/adLogo'
import { getTemplate } from './helpers/getTemplate'
import type { ImaPlayer } from '../adPlayer/ImaPlayer'
import type { Slot } from '../slots/Slot'
import { triggerPixel } from '../helpers/DOM/triggerPixel'
import createScriptTag from '../helpers/DOM/createScriptTag'
import EventManager from '../eventManager/EventManager'
import insertScript from '../helpers/DOM/insertScript'

interface INativeParams {
  slot: Slot
  bidResponse: IBidResponse
}

export class NativeRenderer implements INativeParams {
  slot: Slot
  bidResponse: IBidResponse

  constructor({ slot, bidResponse }: INativeParams) {
    this.slot = slot
    this.bidResponse = bidResponse
  }

  render() {
    renderNativeAd(this)
    writeImgTrackers(this)
    writeJsTracker(this)
    handleImpressionTrackers(this)
  }
}

export const renderNativeAd = async ({
  slot,
  bidResponse: { native }
}: INativeParams): Promise<HTMLElement> => {
  const target = slot.wrapper
  const nativeAd = buildNativeAd(slot, native)

  if (!target) {
    return nativeAd
  }

  target.prepend(nativeAd)

  if (native?.video && (native.video.vastUrl || native.video.vastXml)) {
    const player = await insertVideo(native, nativeAd)
    player.onEvent(player.events.adError, player.cleanup)
  }

  return nativeAd
}

export const buildNativeAd = (
  slot: Slot,
  nativeResponse: INativeResponse | undefined
): HTMLElement => {
  const nativeAd = document.createElement('div')

  nativeAd.classList.add(nativeWrapperSelector)
  nativeAd.innerHTML = getTemplate(slot)
  insertAdLogo(nativeAd)

  const dataEls = nativeAd.querySelectorAll(`[data-native-type]`)
  dataEls.forEach((el: HTMLElement) => insertNativeData(nativeResponse, el))
  nativeAd.querySelectorAll('img').forEach((img) => {
    img.classList.add('ggnoads')
  })

  return nativeAd
}

export const writeImpressionTracker = ({ method, url }: IEventTracker) => {
  switch (method) {
    case EventMethod.img:
      triggerPixel(url)
      break
    case EventMethod.js:
      const script = createScriptTag(url)
      insertScript(script)
      break
    default:
      break
  }
}

export const handleImpressionTrackers = ({
  bidResponse: { native },
  slot
}: INativeParams) => {
  if (!native) {
    return
  }
  const { eventTrackers } = native
  if (!eventTrackers) {
    return
  }

  eventTrackers.forEach((tracker) => {
    switch (tracker.event) {
      case EventType.impression:
        writeImpressionTracker(tracker)
        break
      case EventType.viewableMRC50:
        const eventListener = EventManager.on(
          EventManager.events.impressionViewable,
          (s) => {
            if (s !== slot) {
              return
            }
            writeImpressionTracker(tracker)
            eventListener()
          }
        )
        break
      default:
        break
    }
    // write impressions (1) right away
    // trigger viewable impressions (2) on impressionViewable event
  })
}

const impressionTrackerMap = {
  [EventType.impression]: (url, type) => {},
  [EventType.viewableMRC50]: (url, type) => {}
}

export const attachClickTrackers = (
  native: INativeResponse,
  el: HTMLElement
) => {
  const html: Array<string> = []
  const { clickTrackers } = native

  clickTrackers?.forEach((src) => {
    html.push(`<script type="text/javascript" src=${src}></script>`)
  })

  el.setAttribute('role', 'link')
  el.setAttribute('tabindex', '0')
  el.setAttribute('rel', 'nonopener sponsor')
  el.style.cursor = 'pointer'
  el.setAttribute('href', native.clickUrl || '')

  el.addEventListener('click', () => {
    clickTrackers?.forEach((tracker) => triggerPixel(tracker))
    window.open(native.clickUrl, '_blank')
  })
}

export const insertNativeData = (
  native: INativeResponse | undefined,
  el: HTMLElement
): void => {
  if (!native) {
    return
  }
  const type = el.getAttribute('data-native-type') || ''
  const value = native[type] || ''
  if (!value) {
    el.style.display = 'none'
    return
  }
  switch (type) {
    case 'image':
      if (native.video && (native.video.vastUrl || native.video.vastXml)) {
        el.style.display = 'none'
        break
      }
      el.style.display = 'block'
      el.style['backgroundImage'] = `url(${value.url})`
      if (value.width && value.height) {
        /** Try to maintain original aspect ratio if possible */
        el.style.paddingBottom = `${(value.height / value.width) * 100}%`
      } else {
        /** Else default to 300px */
        el.style.height = '300px'
      }
      attachClickTrackers(native, el)
      break
    case 'icon':
      el.setAttribute('src', value.url)
      attachClickTrackers(native, el)
      break
    case 'clickUrl':
      el.setAttribute('href', value)
      el.setAttribute('rel', 'sponsered noopener')
      el.setAttribute('target', '_blank')
      attachClickTrackers(native, el)
      break
    case 'sponsoredBy':
      el.innerText += value
      attachClickTrackers(native, el)
      break
    case 'title':
      el.innerText = value
      attachClickTrackers(native, el)
      break
    case 'video':
      el.style.display = 'block'
      break
    default:
      el.innerText = value
      break
  }
}

export const insertAdLogo = (el: Element) => {
  const logoElement = createElement(adLogo)
  el.appendChild(logoElement)
}

/** Elements need to be appended to the DOM before trying to render */
export const insertVideo = async (
  native: INativeResponse,
  nativeAd: HTMLElement
): Promise<ImaPlayer> => {
  const videoEl: HTMLDivElement | null =
    nativeAd.querySelector(`[data-native-type="video"]`) ||
    document.createElement('div')
  const { width } = videoEl.getBoundingClientRect()
  // eslint-disable-next-line no-magic-numbers
  const ratio = 9 / 16
  const height = width * ratio
  videoEl.style.height = `${height}px`

  const { ImaPlayer } = await import(
    /* webpackChunkName: "afterScroll"*/ '../adPlayer/ImaPlayer'
  )

  const player = await ImaPlayer.new({
    container: videoEl as HTMLElement,
    autoplay: true,
    muted: true,
    height,
    width
  })

  const vastXml = native.video?.vastXml || ''
  player.enableViewablePlayMode()
  player.initVideoAd({ vastXml })

  return player
}

export const removePlaceholderContent = ({ slot }: INativeParams) => {
  const target = slot.target || document.createElement('div')
  const children = Array.from(target.children)
  children.forEach((c: Element) => {
    if (!c.classList.contains('adunitwrapper')) {
      c.remove()
    }
  })
}

export function writeImgTrackers({ bidResponse, slot }: INativeParams): void {
  var native = bidResponse.native as INativeResponse
  var impressionTrackers = native.impressionTrackers || []

  impressionTrackers.forEach((src) => {
    triggerPixel(src)
  })
}

/**
 * I'm so sorry for this. A script tag string is returned as jstracker. Its
 * responsible for viewability, etc. If we apply it as innerHTML somewhere,
 * the browser will not execute it. SO we have to seek out the src URL with
 * some regex and make a script tag ourselves with it.
 */
const writeJsTracker = ({
  bidResponse: { native },
  slot
}: INativeParams): void => {
  if (!native || !native.jstracker) {
    return
  }
  const { jstracker } = native
  const interpolatedTracker = jstracker.replace(
    '%native_dom_id%',
    slot.wrapperId
  )
  const match = interpolatedTracker?.match(/src=["'](.*)["']\s/)
  const src = (match && match[1]) || ''
  const script = document.createElement('script')
  script.setAttribute('src', src)
  slot.wrapper.appendChild(script)
}

/** Input a template string, get a dom node back */
export function createElement(template: string): Element {
  const placeholder = document.createElement('div')
  placeholder.innerHTML = template

  return placeholder.children[0] || document.createElement('div')
}
